import { maybeWebContext } from "../search";
// chat/brain.ts — the chat "brain", SERVER-FIRST. askBrain() sends the user's message + this app's
// declared action catalog (CHAT_ACTIONS) + a SMALL on-device data summary + recent turns to the
// generic server chat brain — POST /api/apps/workout_tracker/chat, ONE billed call via the app's
// existing brainCall (JWT-authed, Managed AI / BYOK) — which returns { action, params, reply }.
// toAction() then defensively coerces the loose params (types, dates, numbers) and resolves
// by-name references (exercise names → preset slugs, workout names → local row ids) against the
// on-device store BEFORE anything executes; missing/unresolvable required params collapse to a
// single 'none' nudge. Data stays local — only the message + the small text summary transit.
//
// The ORIGINAL local regex/keyword router (routeMessage, below) is kept FULLY INTACT as the free
// fallback: signed-out, offline, out of funds, or any server failure falls straight back to it, so
// the chat always answers (and never bills). Its locally-resolved intents:
//   • "summar… / trends / how am I doing"   → summarize (executes via summarize_trends)
//   • "log bench 100 x 5" / "add squat 140x3" → recognize exercise + numbers    → add_set
//   • "start push day" / "new workout"        → light client intent             → add_workout
//   • "show/find/list … this week"            → local filter over listWorkouts    → filter
//   • "prs / personal records / best lifts"   → local listPRs                     → list_prs
//   • "delete <workout>" / "delete last set"  → resolve id locally → delete_* (UI confirms)
//   • anything else                           → none + a short clarifying nudge

import { store, type Workout, type Routine, type RoutineExercise } from "../store";
import { presetExercises, presetBySlug, type PresetExercise } from "../exercises";
import { formatWeight } from "../policy";
import { BrainError, brainCall } from "../brain";
import type { ChatAction, ResultRow, WorkoutFilter } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: ResultRow[]; // assistant turns may carry filter / PR matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage/resolve* build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope`. Workout is fitness-adjacent — the scope keeps the reply
// channel observational and declines training-program prescription / medical-injury advice.
const SCOPE =
  "This app is a private workout log: log workouts + sets, delete/search them, show PRs, and give an " +
  "OBSERVATIONAL summary of the user's OWN logged training (volume, PRs, frequency); back up to their own cloud. " +
  "Describe their logged data; never prescribe a training program or set targets, and never give medical, injury, " +
  "or rehab advice — point to a qualified professional for those.";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : "";

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

/**
 * The pre-LLM training/medical-advice fence (row 12). Was PROMPT-ONLY before this pass — SCOPE
 * above told the server brain not to prescribe, but nothing stopped an advice-seeking question
 * from reaching the model in the first place (§5c: prompting alone does not hold). Deliberately
 * conservative: matches advice-INTENT phrasings ("should I deload", "what program should I run",
 * "is this pain normal"), not factual questions that merely mention training ("how much did I lift
 * last time" passes through).
 */
export function isTrainingAdviceQuestion(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  const PATTERNS: RegExp[] = [
    /\bshould i\b/, // should I deload / increase weight / add a rest day
    /\bwhat should i\b/,
    /\bwhat (program|routine|split|plan) should i\b/,
    /\bwhat should i (lift|train|do) next\b/,
    /\bhow (much|many) (weight|reps|sets) should i\b/,
    /\bam i overtraining\b/,
    /\bis (it|this) (normal|okay|ok|safe) to (train|lift|push|work out)\b/,
    /\bis my (\w+ )?(pain|injury|form|technique) (normal|ok|okay|serious|bad)\b/,
    /\bcan i (push through|train through|work through) (this )?(the )?pain\b/,
    /\bhow do i fix my (\w+ )?(form|technique)\b/, // "fix my SQUAT form" (fence-battery 2026-08-26)
    /\bwhat (weight|reps|sets) (should|do) i (use|do|lift)\b/,
    /\bis .{0,15}(too much|too heavy|too light)\b/,
    /\bshould i see a (doctor|physio|physical therapist|professional)\b/,
    /\bwhat would you (recommend|suggest|advise)\b/,
    // Gaps closed by fence-battery 2026-08-26 — each pattern is INTENT-PHRASED (an asking shape,
    // never a bare token), so logs that merely mention the same words still pass ("log shoulder
    // press", "knee felt sore on set 3", "give me a workout summary"):
    /\bhow (heavy|hard|often|long|frequently) should\b/,             // "how heavy should my squat be"
    /\bis (it|this) (bad|good|fine|dangerous|harmful) to (train|lift|push|work out|skip)\b/, // "is it bad to train every day"
    /\bwhat('?s| is)? a good\b.{0,20}\b(weight|program|routine|split|reps?|sets?|exercises?|warm ?up)\b/, // "what's a good starting weight" (normalizer keeps apostrophes)
    /\b(hurts?|pain|sore|injur\w*|tweaked)\b.{0,60}\bwhat (do|should|can) i\b/, // "my knee hurts … what do I do"
    /\bgive me a (workout|routine|program|session)\b(?! (summary|recap|overview))/, // improvised prescription, not a summary ask
    /\bhow (do|can) i (improve|increase|progress|get stronger)\b/,   // "how do I improve my bench"
    /\byou think i (should|need)\b/,                                 // "do you think I should add cardio"
    /\b(recommend|suggest|advise) (a|an|me|some)\b/,                 // bare-verb solicitation
  ];
  return PATTERNS.some((re) => re.test(q));
}

const TRAINING_ADVICE_DECLINED =
  "I can describe your own logged training — volume, frequency, PRs, trends — but I can't tell you " +
  "what to lift next or whether pain/form is normal; that needs a coach or medical professional. " +
  "Try asking what your numbers show, e.g. \"how has my bench trended the last 2 weeks?\" Or try " +
  "\"draft a program\" if you want a full plan — I won't improvise one in chat.";

/**
 * Explicit program-drafting intent (PROGRAM-DESIGN.md §8) — checked BEFORE isTrainingAdviceQuestion
 * so "draft me a program" opens the real feature instead of hitting the generic advice refusal.
 * This is the one shape of "tell me what to do" the fence is actually built to say yes to.
 */
export function isDraftProgramRequest(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  // "redo/change/regenerate my program" reopens the SAME flow as a first-time draft — it's still
  // one AI-drafted proposal the user reviews/edits before anything's saved, never an in-place mutation.
  return /\b(draft|design|build|plan|create|redo|regenerate|change|adjust|redraft)\s+(me\s+)?(a\s+|my\s+)?(program|plan|block)\b/.test(q);
}

/**
 * "Repeat my last workout" (Hevy/Strong staple: recreate a past session instead of typing it in
 * from scratch) — deterministic, no AI call needed, so it's checked the same way: before either
 * the server round-trip or the offline fallback, and it never reaches the advice fence.
 */
export function isRepeatWorkoutRequest(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  return /\b(repeat|redo)\s+(my\s+|the\s+)?(last\s+|previous\s+)?workout\b/.test(q)
    || /\bdo\s+(it|that|this)\s+again\b/.test(q)
    || /\bsame\s+workout\s+as\s+(last\s+time|before|yesterday)\b/.test(q);
}

const HELP =
  "I can log workouts and sets, search your history, show PRs, and summarize your training — just tell me. Try:\n" +
  '• "log bench 100 x 5"\n' +
  '• "start push day"\n' +
  '• "show workouts this week"\n' +
  '• "my PRs"\n' +
  '• "summarize my last 14 days" · "delete last set"';

// ── Exercise recognition ───────────────────────────────────────────────────────
// Common gym shorthand → preset slug. Longest-phrase-wins matching (below) resolves overlaps
// like "row" (barbell-row) vs "rowing" (cardio).
const ALIASES: Record<string, string> = {
  bench: "bench-press", "bench press": "bench-press", "incline bench": "incline-bench-press",
  squat: "back-squat", squats: "back-squat", "front squat": "front-squat",
  deadlift: "deadlift", deadlifts: "deadlift", rdl: "romanian-deadlift",
  ohp: "overhead-press", "shoulder press": "overhead-press", press: "overhead-press",
  row: "barbell-row", rows: "barbell-row",
  curl: "barbell-curl", curls: "barbell-curl",
  pushup: "pushup", pushups: "pushup", "push up": "pushup", "push ups": "pushup",
  pullup: "pullup", pullups: "pullup", "pull up": "pullup", "pull ups": "pullup",
  chinup: "chinup", chinups: "chinup", dip: "dip", dips: "dip",
  lunge: "lunge", lunges: "lunge",
  run: "treadmill-run", running: "treadmill-run", jog: "treadmill-run", jogging: "treadmill-run",
  bike: "cycling", cycle: "cycling", cycling: "cycling",
  rowing: "rowing", elliptical: "elliptical",
};

const normEx = (s: string) => s.toLowerCase().replace(/[-\s]+/g, " ").trim();
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// (phrase → slug) candidates, longest phrase first so multi-word names win over their fragments.
const CANDIDATES: { phrase: string; slug: string }[] = (() => {
  const list: { phrase: string; slug: string }[] = [];
  for (const p of presetExercises) {
    list.push({ phrase: normEx(p.name), slug: p.slug });
    list.push({ phrase: normEx(p.slug), slug: p.slug });
  }
  for (const [k, v] of Object.entries(ALIASES)) list.push({ phrase: normEx(k), slug: v });
  const seen = new Set<string>();
  return list
    .filter((c) => (seen.has(c.phrase) ? false : (seen.add(c.phrase), true)))
    .sort((a, b) => b.phrase.length - a.phrase.length);
})();

function findExerciseInText(text: string): PresetExercise | undefined {
  const t = " " + normEx(text) + " ";
  for (const c of CANDIDATES) {
    if (new RegExp("\\b" + escapeRe(c.phrase) + "\\b").test(t)) return presetBySlug(c.slug);
  }
  return undefined;
}

// Parse weight/reps from a message, respecting what the exercise tracks:
//   strength (weight+reps) → "100 x 5", "100 for 5", or two bare numbers
//   bodyweight (reps only) → "x 20", "20 reps", or a lone number → reps, weight 0
//   cardio (neither)       → a lone number = minutes, stored in the reps field (app convention), weight 0
function parseSetLoad(text: string, preset: PresetExercise): { weight: number; reps: number } | null {
  const tracksWeight = preset.tracksWeight !== false;
  const tracksReps = preset.tracksReps !== false;
  const wr = text.match(/(\d+(?:\.\d+)?)\s*(?:x|×|\*|for|@)\s*(\d+)/i);
  const nums = (text.match(/\d+(?:\.\d+)?/g) ?? []).map(Number);

  if (tracksWeight && tracksReps) {
    if (wr) return { weight: parseFloat(wr[1]), reps: Math.round(parseFloat(wr[2])) };
    if (nums.length >= 2) return { weight: nums[0], reps: Math.round(nums[1]) };
    return null;
  }
  if (!tracksWeight && tracksReps) {
    const repsM = text.match(/(?:x|×|\*)\s*(\d+)/i) || text.match(/(\d+)\s*reps?/i);
    if (repsM) return { weight: 0, reps: Math.round(parseFloat(repsM[1])) };
    if (nums.length >= 1) return { weight: 0, reps: Math.round(nums[nums.length - 1]) };
    return null;
  }
  if (nums.length >= 1) return { weight: 0, reps: Math.round(nums[0]) };
  return null;
}

// "on 2026-06-30" / "yesterday" / "today" → a date + the remaining text.
function parseWhen(s: string): { performed_on: string; rest: string } {
  const iso = s.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (iso) return { performed_on: iso[1], rest: s.replace(iso[0], "").replace(/\bon\b/i, "").trim() };
  if (/\byesterday\b/i.test(s)) {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return { performed_on: d.toISOString().slice(0, 10), rest: s.replace(/\b(on\s+)?yesterday\b/i, "").trim() };
  }
  if (/\btoday\b/i.test(s)) return { performed_on: todayISO(), rest: s.replace(/\b(on\s+)?today\b/i, "").trim() };
  return { performed_on: todayISO(), rest: s };
}

function parseWorkoutFilter(text: string): WorkoutFilter {
  const low = text.toLowerCase();
  const f: WorkoutFilter = {};
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const startOfWeek = (d: Date) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };

  if (/\bthis\s+week\b/.test(low)) f.since = iso(startOfWeek(now));
  else if (/\blast\s+week\b/.test(low)) {
    const s = startOfWeek(now); const from = new Date(s); from.setDate(from.getDate() - 7);
    const to = new Date(s); to.setDate(to.getDate() - 1);
    f.since = iso(from); f.until = iso(to);
  } else if (/\bthis\s+month\b/.test(low)) f.since = iso(new Date(now.getFullYear(), now.getMonth(), 1));
  else if (/\blast\s+month\b/.test(low)) {
    f.since = iso(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    f.until = iso(new Date(now.getFullYear(), now.getMonth(), 0));
  } else if (/\bthis\s+year\b/.test(low)) f.since = iso(new Date(now.getFullYear(), 0, 1));
  else {
    const nd = low.match(/last\s+(\d+)\s+days?/);
    if (nd) { const d = new Date(); d.setDate(d.getDate() - parseInt(nd[1], 10)); f.since = iso(d); }
  }

  const term = low
    .replace(/\b(show|find|list|search|which|how\s+many|count|me|my|all|the|workouts?|sessions?|history|for|with|in|on|recent)\b/g, " ")
    .replace(/\bthis\s+(week|month|year)\b/g, " ")
    .replace(/\blast\s+(?:week|month|\d+\s+days?)\b/g, " ")
    .replace(/[?.!]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (term.length >= 2) f.text = term;
  return f;
}

function listWk(ws: Workout[]): string {
  return ws.slice(0, 5).map((w) => `• ${w.name} — ${fmtDate(w.performed_on)}`).join("\n");
}

// ═══════════════════════════════════════════════════════════════════════════════
// SERVER-FIRST BRAIN — one billed call to the generic /api/apps/workout_tracker/chat
// ═══════════════════════════════════════════════════════════════════════════════

// The action catalog declared to the server brain. It mirrors the local ChatAction union
// EXACTLY (actions.ts executes every one of these on-device) — we never declare an action
// the app can't run. The store has no update command, so there is deliberately no edit action.

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.workout-tracker.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS: { name: string; description: string; params?: Record<string, string> }[] = [
  {
    name: "add_workout",
    description: "Start a new workout session (e.g. 'start push day', 'new leg workout yesterday').",
    params: {
      name: 'the workout name, e.g. "Push Day"; use "Workout" if the user gave none',
      performed_on: "date YYYY-MM-DD; resolve relative words (today, yesterday) against CURRENT DATE",
      notes: "optional free-text note; omit if none",
    },
  },
  {
    name: "add_set",
    description: "Log one set of an exercise (the app opens today's workout automatically if none exists).",
    params: {
      exercise: 'the exercise name as the user said it, e.g. "bench press", "squats"',
      weight: "weight lifted, a number in the user's own unit; 0 for bodyweight exercises",
      reps: "rep count, integer; for cardio exercises put the duration in MINUTES here",
      performed_on: "optional date YYYY-MM-DD; omit for today",
    },
  },
  {
    name: "delete_workout",
    description: "Delete a whole workout session (the app asks the user to confirm before executing).",
    params: { workout: "the workout's name, or a distinctive part of it" },
  },
  {
    name: "delete_set",
    description: "Delete the most recently logged set (the app asks the user to confirm before executing).",
  },
  {
    name: "filter",
    description: "Search or list past workouts by name and/or date range.",
    params: {
      text: "optional search term matched against workout names; omit to list all",
      since: "optional start date YYYY-MM-DD inclusive; resolve 'this week' etc. against CURRENT DATE",
      until: "optional end date YYYY-MM-DD inclusive",
    },
  },
  { name: "list_prs", description: "Show the user's personal records (best lifts, estimated 1RMs)." },
  { name: "plate_calc", description: "Calculate which plates to load on a barbell for a target weight (per side). Pure on-device math. Use for 'what plates for 100kg', 'how do I load 225 lb', 'plates for 140 on a 15kg bar'.", params: { target: "the total target weight, a number", unit: "optional kg or lb (defaults to kg)", bar: "optional bar weight; defaults to 20kg / 45lb" } },
  {
    name: "summarize",
    description: "Summarize the user's recent training trends (volume, frequency, PRs).",
    params: { days: "optional window in days, integer; default 14" },
  },
  { name: "list_routines", description: "List the user's saved routines (reusable workout templates).", params: {} },
  {
    name: "add_routine",
    description: "Save a reusable routine — a named set of exercises the user repeats (e.g. 'save a push day routine with bench, overhead press and dips'). Give it a name and the exercises.",
    params: {
      name: 'the routine name, e.g. "Push Day"',
      exercises: 'the exercise names, comma-separated, e.g. "bench press, overhead press, dips"',
    },
  },
  {
    name: "delete_routine",
    description: "Delete a saved routine by its name (the app asks the user to confirm before executing).",
    params: { routine: "the routine's name, or a distinctive part of it" },
  },
  {
    name: "start_routine",
    description: "Start a new workout from a saved routine — opens today's workout pre-loaded with the routine's exercises. Use for 'start my push day routine', 'do my leg routine'.",
    params: { routine: "the routine's name, or a distinctive part of it" },
  },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (progress photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE workout log instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
];

// Shape the generic server chat route returns; params arrive LOOSE and untyped —
// toAction() below coerces and resolves everything before an action can execute.
interface ServerAct { name?: unknown; action?: unknown; params?: unknown }
interface ServerChatResponse {
  actions?: unknown[];   // the shared brain returns an ARRAY (G17); fall back to single `action`.
  action?: unknown;
  params?: unknown;
  reply?: unknown;
}

// A SMALL app-data summary so the brain can resolve references ("delete tuesday's push day",
// "how's my bench") against what the user actually has. Names only, capped — never raw history.
async function buildContext(): Promise<string> {
  const [workouts, prs] = await Promise.all([store.listWorkouts(50000), store.listPRs()]);
  const lines: string[] = [`Workouts logged: ${workouts.length}`];
  if (workouts.length) {
    const w0 = workouts[0];
    lines.push(`Most recent workout: "${w0.name}" on ${w0.performed_on.slice(0, 10)}`);
    const names = [...new Set(workouts.map((w) => w.name))].slice(0, 40);
    lines.push(`Workout names (newest first): ${names.join(", ")}`);
  }
  if (prs.length) {
    lines.push(`Exercises the user has logged: ${prs.slice(0, 40).map((p) => p.exercise_name).join(", ")}`);
  }
  return lines.join("\n");
}

export async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  // Recent PRIOR turns (everything before the message being answered) give the brain
  // conversational continuity ("delete it" → what "it" was).
  let cut = history.length;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].role === "user" && history[i].content.trim() === text) { cut = i; break; }
  }
  const prior = history.slice(0, cut).slice(-7).map((t) => ({ role: t.role, content: t.content }));
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const r = await brainCall<ServerChatResponse>("chat", {
    message: text + (await maybeWebContext(text)),
    actions: CHAT_ACTIONS,
    context,
    history: prior,
    scope: SCOPE,
  }, onChunk);
  const say = asStr(r.reply);
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? (r.actions as ServerAct[])
    : (r.action != null ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action, params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

// ── Defensive coercion of the server's loose params ────────────────────────────
const asStr = (v: unknown): string =>
  typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : "";
const asNum = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" ? parseFloat(v.replace(/[^0-9.\-]/g, "")) : NaN;
  return Number.isFinite(n) ? n : null;
};
const asInt = (v: unknown): number | null => {
  const n = asNum(v);
  return n === null ? null : Math.round(n);
};
const asDate = (v: unknown): string | null => {
  const m = asStr(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};

// Map the server's { action, params, reply } to a fully-typed, locally-RESOLVED ChatAction.
// Every param is coerced; name references become local ids/slugs via the store; anything
// required-but-unresolvable collapses to 'none' with ONE helpful message.
export async function toAction(r: ServerChatResponse): Promise<One> {
  const name = typeof r.action === "string" ? r.action : "none";
  const p: Record<string, unknown> =
    r.params && typeof r.params === "object" && !Array.isArray(r.params)
      ? (r.params as Record<string, unknown>)
      : {};
  const reply = typeof r.reply === "string" ? r.reply.trim() : "";

  switch (name) {
    case "add_workout": {
      const nm = asStr(p.name) || "Workout";
      const performed_on = asDate(p.performed_on) ?? todayISO();
      const notes = asStr(p.notes);
      return { reply, action: { kind: "add_workout", name: nm, performed_on, notes: notes || null } };
    }

    case "add_set": {
      const exTerm = asStr(p.exercise);
      if (!exTerm) return none(reply || "Which exercise is that set for?");
      const preset = findExerciseInText(exTerm);
      if (!preset) {
        return none(`I couldn't match "${exTerm}" to an exercise in the library — try its full name, e.g. "log bench press 100 x 5".`);
      }
      const tracksWeight = preset.tracksWeight !== false;
      const weight = tracksWeight ? asNum(p.weight) : 0;
      const reps = asInt(p.reps);
      if (reps === null || reps <= 0 || weight === null || weight < 0) {
        const hint = tracksWeight ? `e.g. "log ${preset.name.toLowerCase()} 100 x 5"` : `e.g. "log ${preset.name.toLowerCase()} x 20"`;
        return none(`How many? Give me the numbers — ${hint}.`);
      }
      const performed_on = asDate(p.performed_on) ?? todayISO();
      // Attach to that day's most recent workout if one exists; else the executor opens a new one.
      const ws = await store.listWorkouts(50000);
      const target = ws.find((w) => w.performed_on.slice(0, 10) === performed_on);
      return {
        reply,
        action: {
          kind: "add_set",
          workout_id: target?.id ?? null,
          workout_name: "Workout",
          performed_on,
          exercise_slug: preset.slug,
          exercise_name: preset.name,
          weight,
          reps,
        },
      };
    }

    case "delete_workout": {
      const term = asStr(p.workout);
      if (!term) return none(reply || "Which workout should I delete?");
      const res = await resolveDeleteWorkout(term);
      return res.action.kind === "none" ? res : { ...res, reply };
    }

    case "delete_set": {
      const res = await resolveDeleteLastSet();
      return res.action.kind === "none" ? res : { ...res, reply };
    }

    case "filter": {
      const f: WorkoutFilter = {};
      const text = asStr(p.text);
      if (text) f.text = text;
      const since = asDate(p.since);
      if (since) f.since = since;
      const until = asDate(p.until);
      if (until) f.until = until;
      const label = text || [f.since, f.until].filter(Boolean).join(" → ");
      return { reply, action: { kind: "filter", filter: f, label: label || undefined } };
    }

    case "list_prs":
      return { reply, action: { kind: "list_prs" } };

    case "plate_calc": {
      const target = asNum(p.target);
      if (target === null || target <= 0) return none(reply || 'Give me a target weight — e.g. "what plates for 100kg".');
      const unitRaw = asStr(p.unit).toLowerCase();
      const unit = unitRaw === "lb" || unitRaw === "lbs" ? "lb" : unitRaw === "kg" ? "kg" : undefined;
      const bar = asNum(p.bar);
      return { reply, action: { kind: "plate_calc", target, bar: bar !== null && bar >= 0 ? bar : undefined, unit } };
    }

    case "summarize": {
      const days = asInt(p.days);
      return { reply, action: { kind: "summarize", days: days !== null && days > 0 ? days : undefined } };
    }

    case "list_routines":
      return { reply, action: { kind: "list_routines" } };

    case "add_routine": {
      const nm = asStr(p.name) || "Routine";
      const exText = asStr(p.exercises);
      const parts = exText.split(/[,|]|\band\b/i).map((s) => s.trim()).filter(Boolean);
      const exercises: RoutineExercise[] = [];
      const unmatched: string[] = [];
      for (const part of parts) {
        const preset = findExerciseInText(part);
        if (preset) { if (!exercises.some((e) => e.slug === preset.slug)) exercises.push({ slug: preset.slug, name: preset.name }); }
        else unmatched.push(part);
      }
      if (!exercises.length) return none(reply || `I couldn't match any exercises${unmatched.length ? ` (${unmatched.join(", ")})` : ""} — name them like "bench press, squats, deadlift".`);
      const note = unmatched.length ? ` (couldn't match: ${unmatched.join(", ")})` : "";
      return { reply, action: { kind: "add_routine", input: { name: nm, exercises }, note } };
    }

    case "delete_routine": {
      const term = asStr(p.routine);
      if (!term) return none(reply || "Which routine should I delete?");
      const res = await resolveRoutine(term);
      if (res.problem) return none(res.problem);
      return { reply, action: { kind: "delete_routine", id: res.routine!.id, label: res.routine!.name } };
    }

    case "start_routine": {
      const term = asStr(p.routine);
      if (!term) return none(reply || "Which routine should I start?");
      const res = await resolveRoutine(term);
      if (res.problem) return none(res.problem);
      return { reply, action: { kind: "start_routine", id: res.routine!.id, label: res.routine!.name } };
    }

    case "backup_save":
      return { reply, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) || asStr(p.contentKind);
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) || asStr(p.content) || undefined, destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(reply || "What should I remember?");
      return { reply, action: { kind: "remember", instruction } };
    }
    case "none":
    default:
      return none(reply || HELP);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }
  // Explicit program-drafting intent is checked FIRST — it's the one thing the fence below
  // still says yes to, and routing it locally means it works even offline/signed-out.
  if (isDraftProgramRequest(text)) return { reply: "", actions: [{ kind: "open_program_flow" }] };
  if (isRepeatWorkoutRequest(text)) return { reply: "", actions: [{ kind: "repeat_workout" }] };
  // Fence runs BEFORE either path (server or offline fallback) — a network failure must never
  // open the gate to an advice-seeking question reaching the model on retry.
  if (isTrainingAdviceQuestion(text)) { const r = none(TRAINING_ADVICE_DECLINED); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Signed-out / offline / out of funds / any server trouble → the free local regex
    // router below, so the chat always answers (and never bills on the fallback path).
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) SUMMARIZE — the one billed intent → existing summarize_trends endpoint (executed in actions.ts).
  if (/\b(summar(?:y|ise|ize)|overview|recap|how\s+am\s+i\s+doing|how'?s?\s+my\s+training|trends?|progress)\b/i.test(low)) {
    const nd = low.match(/last\s+(\d+)\s+days?/);
    return { reply: "", action: { kind: "summarize", days: nd ? parseInt(nd[1], 10) : undefined } };
  }

  // 2) PRs — local read.
  if (/\b(prs?|personal\s+records?|best\s+lifts?|my\s+records?|one\s+rep\s+max(?:es)?)\b/i.test(low)
      && !/\b(add|log|record|new|delete|remove|start)\b/i.test(low)) {
    return { reply: "", action: { kind: "list_prs" } };
  }

  // 3) DELETE last set — resolve the most recent set locally; UI confirms.
  if (/\b(delete|remove|undo)\s+(?:the\s+|my\s+)?last\s+set\b/i.test(low)) {
    return resolveDeleteLastSet();
  }

  // 4) DELETE workout — resolve by name locally; UI confirms.
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(.+)$/i);
  if (delM) {
    const term = delM[1].trim().replace(/^(?:workout|session)\s+/i, "").replace(/[.?!]+$/, "");
    return resolveDeleteWorkout(term);
  }

  // 5) ADD WORKOUT — "start/new/create workout [named X]" or "log a <push/leg/…> day".
  const wkM =
    t.match(/^(?:new|start|begin|create|log|add|record)\s+(?:a\s+|an\s+)?(?:workout|session)\b\s*(?:(?:called|named|titled)\s+)?(.*)$/i) ||
    t.match(/^(?:new|start|begin|create|log|add|record)\s+(?:a\s+|an\s+)?((?:push|pull|legs?|leg|upper|lower|full[\s-]?body|chest|back|shoulders?|arms?|core|cardio)\s+day)\b(.*)$/i);
  if (wkM) {
    const raw = ((wkM[1] ?? "") + " " + (wkM[2] ?? "")).trim();
    const { performed_on, rest } = parseWhen(raw);
    let name = rest.replace(/^(?:called|named|titled)\s+/i, "").replace(/^["']|["']$/g, "").replace(/\bon\s*$/i, "").trim();
    if (!name) name = "Workout";
    return { reply: "", action: { kind: "add_workout", name, performed_on } };
  }

  // 6) ADD SET — a recognized exercise + numbers (with an explicit log verb OR any digit present).
  const setLead = t.match(/^(?:log|add|record|did|hit)\s+(.+)$/i);
  const body = setLead ? setLead[1] : t;
  const preset = findExerciseInText(body);
  if (preset && (setLead || /\d/.test(body))) {
    const load = parseSetLoad(body, preset);
    if (!load) {
      const hint = preset.tracksWeight === false ? `e.g. "log ${preset.name.toLowerCase()} x 20"` : `e.g. "log ${preset.name.toLowerCase()} 100 x 5"`;
      return none(`How many? Give me the numbers — ${hint}.`);
    }
    // Attach to today's most-recent workout if there is one; otherwise the executor opens a new one.
    const ws = await store.listWorkouts(50000);
    const today = todayISO();
    const workout_id = ws.length && ws[0].performed_on.slice(0, 10) === today ? ws[0].id : null;
    return {
      reply: "",
      action: {
        kind: "add_set",
        workout_id,
        workout_name: "Workout",
        performed_on: today,
        exercise_slug: preset.slug,
        exercise_name: preset.name,
        weight: load.weight,
        reps: load.reps,
      },
    };
  }

  // 7) FILTER / SEARCH — query-shaped → local filter over listWorkouts.
  if (/\b(show|find|list|search|which|how\s+many|count|workouts?|sessions?|history|this\s+week|last\s+week|this\s+month|last\s+month|this\s+year|recent)\b/i.test(low)) {
    const filter = parseWorkoutFilter(t);
    return { reply: "", action: { kind: "filter", filter, label: filter.text || (filter.since ? "your filter" : t) } };
  }

  // 8) Unclear → a short clarifying nudge.
  return none(HELP);
}

// ── Shared reference-resolution (used by BOTH the server path and the local router) ──

// The most recent set of the most recent workout; the UI confirms before executing.
async function resolveDeleteLastSet(): Promise<One> {
  const ws = await store.listWorkouts(50000);
  if (!ws.length) return none("There are no workouts yet.");
  const detail = await store.getWorkout(ws[0].id);
  const sets = detail?.sets ?? [];
  if (!sets.length) return none(`"${ws[0].name}" has no sets to delete.`);
  const s = sets[sets.length - 1];
  const load = s.weight > 0 ? `${formatWeight(s.weight)} × ${s.reps}` : `${s.reps}`;
  return { reply: "", action: { kind: "delete_set", id: s.id, label: `${s.exercise_name} ${load}` } };
}

// Routine name → the local Routine; single-match only (fuzzy, fleet standard).
async function resolveRoutine(term: string): Promise<{ routine?: Routine; problem?: string }> {
  const routines = await store.listRoutines();
  if (!routines.length) return { problem: 'You have no saved routines yet — say e.g. "save a push day routine with bench, squats".' };
  const matches = findNamedMatches(routines, term);
  if (!matches.length) return { problem: notFoundMessage(routines, term) };
  if (matches.length > 1) return { problem: `I found ${matches.length} routines matching "${term}" — be more specific:\n${matches.slice(0, 5).map((r) => `• ${r.name}`).join("\n")}` };
  return { routine: matches[0] };
}

// Workout name → local row id; single-match only (ambiguity asks the user to narrow it). Forgiving
// name lookup (FLEET STANDARD, see ./fuzzy) — tolerant of misspellings, dropped words and word order.
async function resolveDeleteWorkout(term: string): Promise<One> {
  const ws = await store.listWorkouts(50000);
  const matches = findNamedMatches(ws, term);
  if (!matches.length) return none(notFoundMessage(ws, term) + ' (To remove a single set, say "delete last set".)');
  if (matches.length > 1) return none(`I found ${matches.length} workouts matching "${term}" — be more specific:\n${listWk(matches)}`);
  const w = matches[0];
  return { reply: "", action: { kind: "delete_workout", id: w.id, label: `${w.name} (${fmtDate(w.performed_on)})` } };
}
